import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap, ClipboardCheck, Award, ListChecks,
  CheckCircle2, Circle, Lock, ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../components/TopHeader";
import api from "../api";

function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`} {...rest}>{children}</div>;
}

const toTimeline = (items) => {
  let current = false;
  return items.map((i) => {
    let state = i.status === "completed" ? "complete" : i.status === "in_progress" ? "inprogress" : "locked";
    if (state === "inprogress") current = true;
    else if (state === "locked" && !current) { state = "inprogress"; current = true; }
    return { n: i.n, label: i.title, state, route: i.route };
  });
};

function Timeline({ timeline, navigate }) {
  return (
    <Card data-testid="onboarding-timeline">
      <h2 className="text-base font-bold text-slate-900">Onboarding timeline</h2>
      <p className="text-sm text-slate-500">Live from your verified onboarding checklist.</p>
      <div className="mt-4 space-y-2">
        {timeline.length === 0 && <div className="text-sm text-slate-400">Loading your progress…</div>}
        {timeline.map((st) => {
          const clickable = st.state !== "locked" && st.route;
          return (
            <div key={st.n} data-testid={`timeline-stage-${st.n}`} onClick={() => clickable && navigate(st.route)} className={`flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 ${clickable ? "cursor-pointer hover:bg-slate-50" : ""}`}>
              <div className="flex items-center gap-3">
                {st.state === "complete" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  : st.state === "inprogress" ? <Circle className="h-5 w-5 text-violet-500" />
                  : <Lock className="h-4 w-4 text-slate-300" />}
                <span className={`text-sm ${st.state === "locked" ? "text-slate-400" : "font-medium text-slate-800"}`}>{st.n}. {st.label}</span>
              </div>
              <StatusBadge status={st.state === "complete" ? "Complete" : st.state === "inprogress" ? "In progress" : "Locked"} />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [manager, setManager] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [sum, setSum] = useState(null);
  const { items: notifications } = useNotifications();

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/my-manager").then((r) => setManager(r.data)).catch(() => {});
    api.get("/onboarding").then((r) => setTimeline(toTimeline(r.data))).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 3))).catch(() => {});
    api.get("/me/summary").then((r) => setSum(r.data)).catch(() => {});
  }, []);

  const pct = d?.onboarding_percent ?? user?.onboarding_percent ?? 0;
  const stats = [
    { key: "training", label: "Onboarding", value: `${d?.completed ?? 0}/${d?.total ?? 13}`, sub: `${pct}% of steps`, Icon: GraduationCap },
    { key: "assessment", label: "Assessment", value: sum?.assessment_score != null ? `${sum.assessment_score}%` : "Not taken", sub: "80% required to pass", Icon: ClipboardCheck },
    { key: "certification", label: "Certification", value: sum?.certified ? "Certified" : "Pending", sub: "Customer Service Representative", Icon: Award },
    { key: "profile", label: "Profile", value: `${d?.profile_completion ?? user?.profile_completion ?? 0}%`, sub: "Profile completion", Icon: ListChecks },
  ];

  return (
    <div className="space-y-6">
      <div className="status-banner-gradient relative overflow-hidden rounded-2xl p-6 text-white md:p-8">
        <div className="absolute inset-0 dot-grid opacity-30" />
        <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-400">Current status — {d?.status || "New Applicant"}</div>
            <div className="mt-1 text-3xl font-extrabold" data-testid="dashboard-percent">{pct}% complete</div>
            <div className="mt-1 text-sm text-white/70">Next required step: {d?.next_step || "Complete Your Profile"}</div>
          </div>
          <Button onClick={() => navigate("/onboarding")} data-testid="dashboard-continue-btn" className="btn-primary-gradient border-0 text-white md:self-start">Continue</Button>
        </div>
        <div className="relative mt-5">
          <div className="h-2 w-full overflow-hidden rounded-full bg-white/15">
            <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 text-xs text-white/50">{d?.completed ?? 0} of {d?.total ?? 13} workflow steps completed</div>
        </div>
      </div>

      {manager?.assigned && (
        <Card data-testid="my-contact-card" className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-violet-600">Your Campbell Communications Contact</div>
            <div className="mt-1 text-lg font-bold text-slate-900">{manager.name}</div>
            <div className="text-sm capitalize text-slate-500">{manager.role}{manager.email ? ` · ${manager.email}` : ""}</div>
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100 text-sm font-bold text-violet-700">
            {(manager.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.key} data-testid={`dashboard-stat-${s.key}`}>
            <div className="flex items-start justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{s.label}</span>
              <s.Icon className="h-4 w-4 text-violet-500" />
            </div>
            <div className="mt-3 text-2xl font-bold text-slate-900">{s.value}</div>
            <div className="mt-1 text-xs text-slate-500">{s.sub}</div>
          </Card>
        ))}
      </div>

      <Card>
        <h2 className="text-base font-bold text-slate-900">Today's task</h2>
        <p className="text-sm text-slate-500">Automatically determined from your onboarding workflow.</p>
        <div className="mt-4 flex items-center justify-between rounded-xl border border-violet-100 bg-violet-50/60 p-4">
          <div>
            <div className="font-semibold text-slate-900">{d?.next_step || "Complete Your Profile"}</div>
            <div className="text-sm text-slate-500">Continue your onboarding to unlock the next stage.</div>
          </div>
          <Button size="sm" onClick={() => navigate("/onboarding")} data-testid="dashboard-go-btn" className="btn-primary-gradient border-0 text-white">Go</Button>
        </div>
      </Card>

      <Timeline timeline={timeline} navigate={navigate} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-base font-bold text-slate-900">Quick actions</h2>
          <div className="mt-4 space-y-2">
            {[{ label: "Continue Onboarding", path: "/onboarding" }, { label: "Upload Documents", path: "/documents" }, { label: "Set Availability", path: "/availability" }, { label: "Get Support", path: "/support" }].map((a) => (
              <button key={a.path} onClick={() => navigate(a.path)} data-testid={`quick-action-${a.path.slice(1)}`} className="flex w-full items-center justify-between rounded-xl border border-slate-100 px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                {a.label}<ArrowRight className="h-4 w-4 text-slate-400" />
              </button>
            ))}
          </div>
        </Card>
        <Card data-testid="dashboard-announcements">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">Announcements</h2>
            <button onClick={() => navigate("/announcements")} data-testid="announcements-view-all" className="text-sm font-medium text-violet-600 hover:underline">View all</button>
          </div>
          <div className="mt-4 space-y-3">
            {announcements.length === 0 && <div className="text-sm text-slate-400">No announcements yet.</div>}
            {announcements.map((a) => (
              <div key={a.id} className="rounded-xl border border-slate-100 p-3">
                <div className="text-sm font-semibold text-slate-800">{a.title}</div>
                <div className="mt-1 line-clamp-2 text-xs text-slate-500">{(a.body || "").replace(/<[^>]+>/g, "")}</div>
                <div className="mt-1 text-[11px] text-slate-400">{a.date}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card data-testid="dashboard-notifications">
        <h2 className="text-base font-bold text-slate-900">Recent notifications</h2>
        <p className="text-sm text-slate-500">Generated automatically as you progress.</p>
        <div className="mt-4 space-y-2">
          {notifications.length === 0 && <div className="text-sm text-slate-400">No notifications yet — they'll appear here as your onboarding progresses.</div>}
          {notifications.slice(0, 5).map((n) => (
            <div key={n.id} onClick={() => n.link && navigate(n.link)} className={`rounded-xl border border-slate-100 p-3 ${n.link ? "cursor-pointer hover:bg-slate-50" : ""}`}>
              <div className="text-sm font-medium text-slate-800">{n.title}</div>
              {n.body && <div className="text-xs text-slate-500">{n.body}</div>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
